import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GridItem from '@/components/GridItem/index.js';
import { useReservationSourceStats } from '@/hooks/useReservationSourceStats';
import { DATE_RANGE_PRESETS, DEFAULT_DATE_RANGE_PRESET, getPresetRange } from '@/utils/DateRangePresets';

// Brand colors so a glance reads "blue = Booking.com" without a legend.
const SOURCE_COLORS = {
  online_store: '#4F46E5',
  booking_com: '#003580',
  expedia: '#FBBF24',
  own: '#0E9F6E',
  unknown: '#94A3B8',
};
const SOURCE_ORDER = ['online_store', 'booking_com', 'expedia', 'own', 'unknown'];

const sumBySource = (stats) => {
  const totals = Object.fromEntries(SOURCE_ORDER.map(s => [s, 0]));
  stats.forEach((day) => {
    SOURCE_ORDER.forEach((s) => {
      totals[s] += day.bySource?.[s] ?? 0;
    });
  });
  return totals;
};

const dayTotal = day => SOURCE_ORDER.reduce((sum, s) => sum + (day.bySource[s] || 0), 0);

const SourceRing = ({ totals, total, size = 72, thickness = 8 }) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  const segments = SOURCE_ORDER.filter(s => totals[s] > 0).map((s) => {
    const fraction = totals[s] / total;
    const segment = {
      key: s,
      color: SOURCE_COLORS[s],
      dasharray: `${fraction * circumference} ${circumference}`,
      dashoffset: -cumulative * circumference,
    };
    cumulative += fraction;
    return segment;
  });

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={thickness}
          style={{ color: 'rgba(0, 0, 0, 0.08)' }}
        />
        {total > 0 && segments.map(segment => (
          <circle
            key={segment.key}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={segment.color}
            strokeWidth={thickness}
            strokeDasharray={segment.dasharray}
            strokeDashoffset={segment.dashoffset}
            strokeLinecap={segments.length > 1 ? 'butt' : 'round'}
          />
        ))}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {total}
        </Typography>
      </Box>
    </Box>
  );
};

const DailyBars = ({ days, maxDaily }) => (
  <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ height: 64, mb: 0.5 }}>
    {days.map((day) => {
      const total = dayTotal(day);
      const heightPct = maxDaily ? Math.max((total / maxDaily) * 100, total > 0 ? 6 : 2) : 2;
      return (
        <Box
          key={day.date}
          title={`${day.date}: ${total}`}
          sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
        >
          <Box
            sx={{
              height: `${heightPct}%`,
              minHeight: 2,
              borderRadius: '2px 2px 0 0',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column-reverse',
              bgcolor: total === 0 ? 'action.hover' : 'transparent',
            }}
          >
            {SOURCE_ORDER.map(s => (
              day.bySource[s] > 0 && (
                <Box key={s} sx={{ height: `${(day.bySource[s] / total) * 100}%`, bgcolor: SOURCE_COLORS[s] }} />
              )
            ))}
          </Box>
        </Box>
      );
    })}
  </Stack>
);

const SourceBreakdownList = ({ totals, total, activeSource, onToggle, t }) => {
  const ranked = useMemo(
    () => SOURCE_ORDER
      .map(s => ({ key: s, color: SOURCE_COLORS[s], count: totals[s] }))
      .sort((a, b) => b.count - a.count),
    [totals],
  );

  return (
    <List dense disablePadding>
      {ranked.map((source) => {
        const pct = total ? Math.round((source.count / total) * 100) : 0;
        const isActive = activeSource === source.key;
        return (
          <ListItemButton
            key={source.key}
            dense
            disableGutters
            onClick={() => onToggle(source.key)}
            sx={{
              'px': 0,
              'py': 0.5,
              'opacity': activeSource && !isActive ? 0.4 : 1,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: source.color, mr: 1.25, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flexGrow: 1, color: 'text.secondary' }}>
              {t(`reservationSources.${source.key}`)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', mr: 1 }}>
              {pct}
              %
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 20, textAlign: 'right' }}>
              {source.count}
            </Typography>
          </ListItemButton>
        );
      })}
    </List>
  );
};

const ReservationSourceTile = () => {
  const { t } = useTranslation();
  const { stats, loading, error, loadStats } = useReservationSourceStats();
  const [open, setOpen] = useState(false);
  const [activeSource, setActiveSource] = useState(null);
  const [preset, setPreset] = useState(DEFAULT_DATE_RANGE_PRESET);

  useEffect(() => {
    const { alku, loppu } = getPresetRange(preset);
    loadStats(alku, loppu);
  }, [preset, loadStats]);

  const handlePresetChange = (event, nextPreset) => {
    if (nextPreset) setPreset(nextPreset);
  };

  const chronological = useMemo(() => [...stats].reverse(), [stats]);
  const totals = useMemo(() => sumBySource(stats), [stats]);
  const total = useMemo(() => Object.values(totals).reduce((a, b) => a + b, 0), [totals]);
  const maxDaily = useMemo(
    () => Math.max(1, ...chronological.map(dayTotal)),
    [chronological],
  );

  const handleClose = () => {
    setOpen(false);
    setActiveSource(null);
  };

  return (
    <>
      <GridItem
        role="button"
        tabIndex={0}
        aria-label={t('reservationSources.tileLabel')}
        clickable
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
      >
        <SourceRing totals={totals} total={loading || error ? 0 : total} />
        <Typography variant="body2" sx={{ color: 'primary.main' }}>
          {t('reservationSources.tileLabel')}
        </Typography>
      </GridItem>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {t('reservationSources.title')}
            </Typography>
            <IconButton size="small" onClick={handleClose} sx={{ mr: -1 }}>
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Stack>

          <ToggleButtonGroup
            value={preset}
            exclusive
            onChange={handlePresetChange}
            size="small"
            fullWidth
            sx={{ mb: 1.5 }}
          >
            {DATE_RANGE_PRESETS.map(p => (
              <ToggleButton key={p.id} value={p.id} sx={{ textTransform: 'none' }}>
                {t(p.labelKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && !loading && (
            <Typography variant="caption" color="error">
              {t('reservationSources.loadError')}
            </Typography>
          )}

          {!loading && !error && total === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {t('reservationSources.empty')}
            </Typography>
          )}

          {!loading && !error && total > 0 && (
            <>
              <DailyBars days={chronological} maxDaily={maxDaily} />
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.disabled">
                  {chronological[0].date}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {chronological[chronological.length - 1].date}
                </Typography>
              </Stack>
              <SourceBreakdownList
                totals={totals}
                total={total}
                activeSource={activeSource}
                onToggle={key => setActiveSource(prev => (prev === key ? null : key))}
                t={t}
              />
            </>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ReservationSourceTile;
